import React, {
  useState,
  useRef,
  useEffect,
  useContext,
  useCallback,
  useMemo,
} from "react";
import ReactDOM from "react-dom";
import {
  FaSearch,
  FaCode,
  FaTimes,
  FaTrash,
  FaExchangeAlt,
  FaKey,
  FaArrowRight,
} from "react-icons/fa";
import { BsLightning } from "react-icons/bs";
import { metadataService, tagService } from "../../../../../../services/api";
import { BaseContext } from "../../../../../../contexts/BaseContext";
import { toast } from "react-hot-toast";
import * as Sentry from "@sentry/react";

export const FindReplacePanel = ({
  isOpen,
  onClose,
  column,
  onRefresh,
  dataslice_id = null,
}) => {
  const dropdownRef = useRef(null);
  const {
    deasyUserConfig,
    vdbLastActiveProfileName,
    llmLastActiveProfileName,
    setSavedTags,
    savedTags,
    selectedDataSlice,
  } = useContext(BaseContext);

  // Workflow state
  const [currentStep, setCurrentStep] = useState(1); // 1: Search, 2: Select Operation, 3: Configure & Execute

  // Search state
  const [searchType, setSearchType] = useState("keyword"); // "keyword" or "regex"
  const [searchPattern, setSearchPattern] = useState("");
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [wholeWordsOnly, setWholeWordsOnly] = useState(true); // Default to true
  const [exactMatch, setExactMatch] = useState(false);

  // Results state
  const [searchResults, setSearchResults] = useState([]);
  const [selectedResults, setSelectedResults] = useState(new Set());
  const [isSearching, setIsSearching] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreResults, setHasMoreResults] = useState(false);
  const [totalResultCount, setTotalResultCount] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const RESULTS_PER_PAGE = 100;

  // Track current loaded count for immediate UI updates
  const [currentLoadedCount, setCurrentLoadedCount] = useState(0);

  // Operation state
  const [operationMode, setOperationMode] = useState("replace"); // "replace" or "delete"
  const [replaceValue, setReplaceValue] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  // Preview state
  const [previewResults, setPreviewResults] = useState([]);

  // Error state
  const [searchError, setSearchError] = useState("");

  // Suggested replacement values state
  const [suggestedReplacementValues, setSuggestedReplacementValues] = useState(
    [],
  );

  // Predefined regex patterns
  const regexPatterns = [
    {
      name: "All Uppercase",
      description:
        "Values that are entirely capitalized (e.g., 'COMPANY', 'INC')",
      pattern: "^[A-Z\\s]+$",
      example: "MICROSOFT, INC., LLC",
    },
    {
      name: "Contains Numbers",
      description: "Values that include any numbers",
      pattern: ".*\\d.*",
      example: "Company123, Office2024",
    },
    {
      name: "No Numbers",
      description: "Values that contain no numbers",
      pattern: "^[^\\d]*$",
      example: "Microsoft, Apple, Google",
    },
    {
      name: "Starts with Number",
      description: "Values that begin with a number",
      pattern: "^\\d.*",
      example: "123Main St, 2024Report",
    },
    {
      name: "Ends with Punctuation",
      description: "Values ending with punctuation (., !, ?, etc.)",
      pattern: ".*[.!?;:,]$",
      example: "Inc., Corp., Ltd.",
    },
    {
      name: "Contains Special Characters",
      description: "Values with special characters (@, #, $, %, etc.)",
      pattern: ".*[@#$%^&*()_+={}\\[\\]|\\\\:;\"'<>,.?/~`].*",
      example: "user@domain.com, $$$, #hashtag",
    },
    {
      name: "Date Format (MM-DD-YYYY)",
      description: "Values that match MM-DD-YYYY date format",
      pattern: "^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])-\\d{4}$",
      example: "03-15-2024, 12-01-2023, 01-31-2022",
    },
    {
      name: "Not a Date (MM-DD-YYYY)",
      description: "Values that do NOT match MM-DD-YYYY date format",
      pattern: "^(?!(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])-\\d{4}$).*",
      example: "Company Name, 15-03-2024, March 15th",
    },
    {
      name: "Email Addresses",
      description: "Email address patterns",
      pattern: "^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$",
      example: "user@example.com, john.doe@company.org",
    },
    {
      name: "Phone Numbers (US)",
      description: "US phone number patterns",
      pattern: "^\\(?\\d{3}\\)?[-\\s.]?\\d{3}[-\\s.]?\\d{4}$",
      example: "(555) 123-4567, 555-123-4567",
    },
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  const buildSearchRegex = useCallback(() => {
    try {
      const flags = caseSensitive ? "" : "i";

      if (searchType === "regex") {
        return new RegExp(searchPattern, flags);
      } else {
        // Keyword search
        const escapedPattern = searchPattern.replace(
          /[.*+?^${}()|[\]\\]/g,
          "\\$&",
        );
        let pattern;
        if (exactMatch) {
          // Exact match - match the entire string
          pattern = `^${escapedPattern}$`;
        } else if (wholeWordsOnly) {
          // Whole words only
          pattern = `\\b${escapedPattern}\\b`;
        } else {
          // Contains match
          pattern = escapedPattern;
        }
        return new RegExp(pattern, flags);
      }
    } catch (error) {
      throw new Error(`Invalid ${searchType} pattern: ${error.message}`);
    }
  }, [searchPattern, searchType, wholeWordsOnly, caseSensitive, exactMatch]);

  const performSearch = async (isLoadMore = false) => {
    if (!searchPattern.trim()) {
      setSearchError("Please enter a value to search for ");
      return;
    }
    setIsSearching(true);

    if (isLoadMore) {
      setIsLoadingMore(true);
    } else {
      setIsSearching(true);
      setSearchError("");
      setSearchResults([]);
      setSelectedResults(new Set());
      setCurrentPage(1);
      setHasMoreResults(false);
      setTotalResultCount(0);
      setCurrentLoadedCount(0);
    }

    try {
      // Build regex pattern
      const regex = buildSearchRegex();

      // Calculate offset for pagination
      const offset = isLoadMore ? (currentPage - 1) * RESULTS_PER_PAGE : 0;
      const limit = RESULTS_PER_PAGE;

      const response = await metadataService.getStandardizeTagDistribution(
        column,
        vdbLastActiveProfileName,
        deasyUserConfig.deasyApiKey,
        dataslice_id || selectedDataSlice?.id || null,
        offset,
        limit,
        regex.toString(),
        caseSensitive,
      );

      // Extract tag_distribution from the response
      const distribution = response.data.tag_distribution || {};
      const uniqueTagCount = response.data.unique_tag_count || 0;
      setTotalResultCount(uniqueTagCount);
      const matchingValues = [];

      // The API already filters results server-side, so we don't need to filter again
      Object.entries(distribution).forEach(([value, stats]) => {
        // Handle the stats structure from getStandardizeTagDistribution
        const count = stats?.count || 0;

        // Ensure value is a string before matching
        const stringValue = typeof value === "string" ? value : String(value);
        const matches = stringValue.match(regex) || [];

        matchingValues.push({
          value: stringValue,
          count: count,
          matches: matches,
        });
      });

      // Sort by count descending
      matchingValues.sort((a, b) => b.count - a.count);

      // Calculate the new total loaded count
      const newTotalLoaded = isLoadMore
        ? currentLoadedCount + matchingValues.length
        : matchingValues.length;

      if (isLoadMore) {
        // Append new results to existing ones
        setSearchResults((prev) => [...prev, ...matchingValues]);
        setCurrentPage((prev) => prev + 1);
        // Update the loaded count immediately for UI display
        setCurrentLoadedCount((prev) => prev + matchingValues.length);
      } else {
        // Set initial results
        setSearchResults(matchingValues);
        setCurrentPage(2); // Next page to load would be page 2
        // Set the initial loaded count
        setCurrentLoadedCount(matchingValues.length);
      }
      setIsSearching(false);
      // Check if there are more results to load
      setHasMoreResults(newTotalLoaded < uniqueTagCount);

      if (!isLoadMore) {
        if (matchingValues.length === 0) {
          // Find the pattern name if it's a preset regex pattern
          const selectedRegexPattern =
            searchType === "regex"
              ? regexPatterns.find((p) => p.pattern === searchPattern)
              : null;

          const displayName = selectedRegexPattern
            ? selectedRegexPattern.name
            : searchPattern;

          toast.error(`No values found matching "${displayName}"`);
        } else {
          const totalMessage =
            uniqueTagCount > matchingValues.length
              ? `Found ${uniqueTagCount} matching values (showing first ${matchingValues.length})`
              : `Found ${uniqueTagCount} matching value${uniqueTagCount > 1 ? "s" : ""}`;

          toast.success(totalMessage);
          setCurrentStep(2);
        }
      } else {
        if (matchingValues.length === 0) {
          // Find the pattern name if it's a preset regex pattern
          const selectedRegexPattern =
            searchType === "regex"
              ? regexPatterns.find((p) => p.pattern === searchPattern)
              : null;

          const displayName = selectedRegexPattern
            ? selectedRegexPattern.name
            : searchPattern;

          toast.error(`No values found matching "${displayName}"`);
        } else {
          const totalMessage =
            uniqueTagCount > matchingValues.length
              ? `Found ${uniqueTagCount} matching values (showing first ${matchingValues.length})`
              : `Found ${uniqueTagCount} matching value${uniqueTagCount > 1 ? "s" : ""}`;

          toast.success(totalMessage);
          setCurrentStep(2);
        }
      }
    } catch (error) {
      console.error("Search error:", error);
      setSearchError(error.message);
      Sentry.captureException(error);
    } finally {
      if (isLoadMore) {
        setIsLoadingMore(false);
      } else {
        setIsSearching(false);
      }
    }
  };

  const generatePreview = () => {
    if (operationMode === "delete") {
      setPreviewResults(
        Array.from(selectedResults).map((value) => ({
          original: value,
          preview: "[DELETED]",
          operation: "delete",
        })),
      );
    } else {
      // Replace operation - replace the entire value, not just the matching portion
      const previews = Array.from(selectedResults).map((value) => {
        return {
          original: value,
          preview: replaceValue, // Replace entire value with replacement value
          operation: "replace",
        };
      });
      setPreviewResults(previews);
    }
    setCurrentStep(3); // Move to final step
  };

  const executeOperation = async () => {
    if (selectedResults.size === 0) {
      toast.error("Please select values to process");
      return;
    }

    if (operationMode === "replace" && !replaceValue.trim()) {
      toast.error("Please enter a replacement value");
      return;
    }

    setIsProcessing(true);

    try {
      if (operationMode === "delete") {
        // Handle deletion - this would need to be implemented in the backend
        await metadataService.deleteMetadataValuesFromTag(
          vdbLastActiveProfileName,
          deasyUserConfig.deasyApiKey,
          column,
          Array.from(selectedResults),
        );
        toast.success(
          `Successfully deleted ${selectedResults.size} value${selectedResults.size > 1 ? "s" : ""}`,
        );
        onRefresh();
        onClose();
      } else {
        // Handle replacement using standardization
        const valueMapping = {};
        const standardizedValue = replaceValue.trim();

        // Create mapping for selected values to the replacement value
        Array.from(selectedResults).forEach((value) => {
          valueMapping[standardizedValue] =
            valueMapping[standardizedValue] || [];
          valueMapping[standardizedValue].push(value);
        });

        // Apply standardization
        await metadataService.standardizeMetadataDB(
          column,
          valueMapping,
          deasyUserConfig.deasyApiKey,
          vdbLastActiveProfileName,
          llmLastActiveProfileName,
        );

        // Find tag details from saved tags
        const tagDetails = savedTags.find((tag) => tag.name === column);
        if (tagDetails) {
          await metadataService.insertStandardization({
            tag_id: tagDetails.tag_id,
            tag_name: column,
            standard_mapping: valueMapping,
            taxonomy_name: `${column} Find & Replace`,
            vdbProfileName: vdbLastActiveProfileName,
            deasyApiKey: deasyUserConfig.deasyApiKey,
          });

          // Update tag with new available values
          const updatedValues = [...(tagDetails.available_values || [])];
          if (!updatedValues.includes(standardizedValue)) {
            updatedValues.push(standardizedValue);
          }
          if (
            tagDetails.available_values &&
            tagDetails.available_values.length > 0
          ) {
            try {
              await tagService.updateTag(
                {
                  ...tagDetails,
                  available_values: updatedValues,
                },
                deasyUserConfig.deasyApiKey,
              );
            } catch (error) {
              if (error?.response?.status === 400) {
                console.warn(
                  `Default tag, ${tagDetails.name} does not allow updates. Skipping...`,
                );
              } else {
                console.error("Error updating tag:", error);
                Sentry.captureException(error);
              }
            }
            // Update saved tags context
            setSavedTags((prevTags) =>
              prevTags.map((tag) =>
                tag.tag_id === tagDetails.tag_id
                  ? { ...tag, available_values: updatedValues }
                  : tag,
              ),
            );
          }
        }

        toast.success(
          `Successfully replaced ${selectedResults.size} value${selectedResults.size > 1 ? "s" : ""} with "${standardizedValue}"`,
        );
        onRefresh();
        onClose();
      }
    } catch (error) {
      console.error("Operation error:", error);
      toast.error("Failed to complete operation. Please try again.");
      Sentry.captureException(error);
    } finally {
      setIsProcessing(false);
    }
  };

  const toggleResultSelection = (value) => {
    const newSelected = new Set(selectedResults);
    if (newSelected.has(value)) {
      newSelected.delete(value);
    } else {
      newSelected.add(value);
    }
    setSelectedResults(newSelected);
  };

  const selectAllResults = () => {
    setSelectedResults(new Set(searchResults.map((r) => r.value)));
  };

  const clearSelection = () => {
    setSelectedResults(new Set());
  };

  const handleSelectRegexPattern = (pattern) => {
    setSearchType("regex");
    setSearchPattern(pattern.pattern);
    setSearchError("");

    // Set case sensitivity based on pattern requirements
    const caseSensitivePatterns = [
      "^[A-Z\\s]+$", // All Uppercase - requires case sensitivity
    ];

    if (caseSensitivePatterns.includes(pattern.pattern)) {
      setCaseSensitive(true);
    }
  };

  const handleSelectSuggestedValue = (value) => {
    setReplaceValue(value);
  };

  const handleLoadMore = () => {
    performSearch(true);
  };

  // Reset all form state when panel closes
  const resetFormState = useCallback(() => {
    setCurrentStep(1);
    setSearchType("keyword");
    setSearchPattern("");
    setCaseSensitive(false);
    setWholeWordsOnly(true);
    setExactMatch(false);
    setOperationMode("replace");
    setReplaceValue("");
    setSearchResults([]);
    setSelectedResults(new Set());
    setPreviewResults([]);
    setIsSearching(false);
    setSearchError("");
    setIsProcessing(false);
    // Reset pagination state
    setCurrentPage(1);
    setHasMoreResults(false);
    setTotalResultCount(0);
    setIsLoadingMore(false);
    setCurrentLoadedCount(0);
  }, []);

  // Reset form when panel closes
  useEffect(() => {
    if (!isOpen) {
      resetFormState();
    }
  }, [isOpen, resetFormState]);

  // Fetch suggested replacement values from tag distribution
  const fetchSuggestedReplacementValues = useCallback(async () => {
    if (!isOpen || !column || operationMode !== "replace") {
      setSuggestedReplacementValues([]);
      return;
    }

    try {
      // Fetch complete distribution using the same API as performSearch
      const response = await metadataService.getStandardizeTagDistribution(
        column,
        vdbLastActiveProfileName,
        deasyUserConfig.deasyApiKey,
        dataslice_id || selectedDataSlice?.id || null,
        0,
        50,
      );

      const tagDistribution = response.data.tag_distribution || {};
      if (!tagDistribution || Object.keys(tagDistribution).length === 0) {
        setSuggestedReplacementValues([]);
        return;
      }

      // Convert to array and sort by count
      const sortedValues = Object.entries(tagDistribution)
        .map(([value, stats]) => {
          // Handle the stats structure from getStandardizeTagDistribution
          const count = stats?.count || 0;

          return {
            value,
            count: count,
          };
        })
        .sort((a, b) => b.count - a.count)
        .slice(0, 8); // Show top 8 suggestions

      setSuggestedReplacementValues(sortedValues);
    } catch (error) {
      console.error("Error fetching suggested values:", error);
      setSuggestedReplacementValues([]);
    }
  }, [
    isOpen,
    column,
    operationMode,
    vdbLastActiveProfileName,
    deasyUserConfig.deasyApiKey,
    dataslice_id,
    selectedDataSlice?.id,
  ]);

  // Fetch suggested values when conditions change
  useEffect(() => {
    fetchSuggestedReplacementValues();
  }, [fetchSuggestedReplacementValues]);

  // Filter suggested values to exclude already selected ones
  const filteredSuggestedValues = useMemo(() => {
    if (suggestedReplacementValues.length === 0) return [];

    const selectedValuesSet = new Set(Array.from(selectedResults));
    return suggestedReplacementValues.filter(
      (item) => !selectedValuesSet.has(item.value),
    );
  }, [suggestedReplacementValues, selectedResults]);

  if (!isOpen) return null;

  const modalContent = (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[1100]"
        onClick={onClose}
      />
      <div
        ref={dropdownRef}
        className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden w-[90vw] max-w-[1000px] h-[85vh] z-[1100] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with Step Indicator */}
        <div className="flex-shrink-0 bg-white border-b border-gray-200 p-6 text-left">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">
                Find & Replace in{" "}
                <span className="text-primary font-bold">{column}</span>
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Search for values using keywords or regex, then replace or
                delete them
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <FaTimes className="w-5 h-5" />
            </button>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center space-x-4">
            <div
              className={`flex items-center ${
                currentStep >= 1 ? "text-primary" : "text-gray-500"
              }`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  currentStep >= 1
                    ? "bg-primary text-white"
                    : "bg-gray-200 text-gray-600"
                }`}
              >
                1
              </div>
              <span
                className={`ml-2 text-sm font-medium ${
                  currentStep >= 1 ? "text-primary" : "text-gray-500"
                }`}
              >
                Search Values
              </span>
            </div>
            <FaArrowRight className="text-gray-400" />
            <div
              className={`flex items-center ${
                currentStep >= 2 ? "text-primary" : "text-gray-500"
              }`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  currentStep >= 2
                    ? "bg-primary text-white"
                    : "bg-gray-200 text-gray-600"
                }`}
              >
                2
              </div>
              <span
                className={`ml-2 text-sm font-medium ${
                  currentStep >= 2 ? "text-primary" : "text-gray-500"
                }`}
              >
                Select Action
              </span>
            </div>
            <FaArrowRight className="text-gray-400" />
            <div
              className={`flex items-center ${
                currentStep >= 3 ? "text-primary" : "text-gray-500"
              }`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  currentStep >= 3
                    ? "bg-primary text-white"
                    : "bg-gray-200 text-gray-600"
                }`}
              >
                3
              </div>
              <span
                className={`ml-2 text-sm font-medium ${
                  currentStep >= 3 ? "text-primary" : "text-gray-500"
                }`}
              >
                Review & Apply
              </span>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Step 1: Search Configuration */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                  <h4 className="font-medium text-gray-900 flex items-center gap-2">
                    <FaSearch className="text-gray-600" />
                    Search Configuration
                  </h4>

                  {/* Search Type Toggle */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setSearchType("keyword");
                        setSearchError("");
                        setSearchPattern("");
                        setExactMatch(false);
                      }}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors border ${
                        searchType === "keyword"
                          ? "bg-primary text-white border-primary"
                          : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      <FaKey className="inline mr-2" />
                      Keyword Search
                    </button>
                    <button
                      onClick={() => {
                        setSearchType("regex");
                        setSearchError("");
                        setSearchPattern("");
                        setCaseSensitive(false);
                        setWholeWordsOnly(false);
                        setExactMatch(false);
                      }}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors border ${
                        searchType === "regex"
                          ? "bg-primary text-white border-primary"
                          : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      <FaCode className="inline mr-2" />
                      Pattern Search
                    </button>
                  </div>

                  {/* Predefined Regex Patterns */}
                  {searchType === "regex" && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3 text-left">
                        Quick Search Patterns
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 overflow-y-auto max-h-[324px]">
                        {regexPatterns.map((pattern, index) => {
                          const isSelected = searchPattern === pattern.pattern;
                          return (
                            <button
                              key={index}
                              onClick={() => handleSelectRegexPattern(pattern)}
                              className={`text-left p-3 border rounded-lg transition-colors ${
                                isSelected
                                  ? "border-primary bg-primary/5"
                                  : "border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                              }`}
                              type="button"
                            >
                              <div className="font-medium text-sm text-gray-900">
                                {pattern.name}
                              </div>
                              <div className="text-xs text-gray-600 mt-1">
                                {pattern.description}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Search Pattern Input */}
                  {searchType === "keyword" && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                        Keyword Search
                      </label>
                      <input
                        type="text"
                        value={searchPattern}
                        onChange={(e) => setSearchPattern(e.target.value)}
                        placeholder="Enter text to search for (e.g., 'INC.')"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                        onKeyDown={(e) => e.key === "Enter" && performSearch()}
                      />
                    </div>
                  )}

                  {/* Search Options */}
                  {searchType === "keyword" && (
                    <div className="space-y-3 text-left">
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Search Options
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <label className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                          <input
                            type="checkbox"
                            checked={caseSensitive}
                            onChange={(e) => setCaseSensitive(e.target.checked)}
                            className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary focus:ring-2 focus:ring-offset-0"
                          />
                          <div className="ml-3">
                            <span className="text-sm font-medium text-gray-700">
                              Case sensitive
                            </span>
                            <p className="text-xs text-gray-500 mt-1">
                              Match letter case exactly
                            </p>
                          </div>
                        </label>
                        <label
                          className={`flex items-center p-3 border rounded-lg transition-colors ${
                            exactMatch
                              ? "border-gray-200 bg-gray-50 cursor-not-allowed"
                              : "border-gray-200 hover:bg-gray-50 cursor-pointer"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={wholeWordsOnly}
                            onChange={(e) =>
                              setWholeWordsOnly(e.target.checked)
                            }
                            className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary focus:ring-2 focus:ring-offset-0 disabled:opacity-50"
                            disabled={exactMatch}
                          />
                          <div className="ml-3">
                            <span
                              className={`text-sm font-medium ${exactMatch ? "text-gray-400" : "text-gray-700"}`}
                            >
                              Whole words only
                            </span>
                            <p
                              className={`text-xs mt-1 ${exactMatch ? "text-gray-400" : "text-gray-500"}`}
                            >
                              Match complete words
                            </p>
                          </div>
                        </label>
                        <label className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                          <input
                            type="checkbox"
                            checked={exactMatch}
                            onChange={(e) => {
                              setExactMatch(e.target.checked);
                              if (e.target.checked) {
                                setWholeWordsOnly(false);
                              }
                            }}
                            className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary focus:ring-2 focus:ring-offset-0"
                          />
                          <div className="ml-3">
                            <span className="text-sm font-medium text-gray-700">
                              Exact match
                            </span>
                            <p className="text-xs text-gray-500 mt-1">
                              Match entire value only
                            </p>
                          </div>
                        </label>
                      </div>
                    </div>
                  )}

                  {searchError && (
                    <div className="text-red-600 text-sm bg-red-50 p-3 rounded-lg border border-red-200">
                      {searchError}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 2: Select Results and Action */}
            {currentStep === 2 && searchResults.length > 0 && (
              <div className="space-y-6">
                {/* Search Results */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-gray-900">
                      {totalResultCount > currentLoadedCount ? (
                        <>
                          Found {totalResultCount} matching value
                          {totalResultCount > 1 ? "s" : ""}
                          (showing {currentLoadedCount})
                        </>
                      ) : (
                        <>
                          Found {currentLoadedCount} matching value
                          {currentLoadedCount > 1 ? "s" : ""}
                        </>
                      )}
                    </h4>
                    <div className="flex gap-2">
                      <button
                        onClick={selectAllResults}
                        className="text-sm text-primary hover:text-primary/80 font-medium"
                      >
                        Select All
                      </button>
                      <button
                        onClick={clearSelection}
                        className="text-sm text-gray-600 hover:text-gray-800"
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg max-h-[400px] overflow-y-auto">
                    {searchResults.map((result, index) => (
                      <div
                        key={index}
                        className={`p-4 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 cursor-pointer ${
                          selectedResults.has(result.value)
                            ? "bg-primary/5 border-primary/20"
                            : ""
                        }`}
                        onClick={() => toggleResultSelection(result.value)}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={selectedResults.has(result.value)}
                            onChange={() => toggleResultSelection(result.value)}
                            className="rounded border-gray-300 text-primary focus:ring-primary focus:ring-2"
                          />
                          <div className="flex-1 ml-2">
                            <div className="font-medium text-gray-900 text-left">
                              {result.value}
                            </div>
                            <div className="text-sm text-gray-600 text-left">
                              {(result.count || 0).toLocaleString()} occurrences
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pagination Controls */}
                  {hasMoreResults && (
                    <div className="flex justify-center pt-4">
                      <button
                        onClick={handleLoadMore}
                        disabled={isLoadingMore}
                        className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                      >
                        {isLoadingMore ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                            Loading more...
                          </>
                        ) : (
                          <>
                            Load More Results
                            {totalResultCount > currentLoadedCount && (
                              <span className="text-sm text-gray-500">
                                ({totalResultCount - currentLoadedCount}{" "}
                                remaining)
                              </span>
                            )}
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>

                {/* Action Selection */}
                {selectedResults.size > 0 && (
                  <div className="bg-gray-50 rounded-lg p-4 space-y-4 text-left">
                    <h4 className="font-medium text-gray-900">
                      Choose Action for {selectedResults.size} selected values
                    </h4>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <button
                        onClick={() => setOperationMode("replace")}
                        className={`p-4 rounded-lg border-2 transition-colors text-left ${
                          operationMode === "replace"
                            ? "border-primary bg-primary/5"
                            : "border-gray-200 hover:border-gray-300 bg-white"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <FaExchangeAlt
                            className={`w-5 h-5 ${
                              operationMode === "replace"
                                ? "text-primary"
                                : "text-gray-400"
                            }`}
                          />
                          <div>
                            <div className="font-medium text-gray-900">
                              Replace Values
                            </div>
                            <div className="text-sm text-gray-600">
                              Replace selected values with a new value
                            </div>
                          </div>
                        </div>
                      </button>

                      <button
                        onClick={() => setOperationMode("delete")}
                        className={`p-4 rounded-lg border-2 transition-colors text-left ${
                          operationMode === "delete"
                            ? "border-red-500 bg-red-50"
                            : "border-gray-200 hover:border-gray-300 bg-white"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <FaTrash
                            className={`w-5 h-5 ${
                              operationMode === "delete"
                                ? "text-red-600"
                                : "text-gray-400"
                            }`}
                          />
                          <div>
                            <div className="font-medium text-gray-900">
                              Delete Values
                            </div>
                            <div className="text-sm text-gray-600">
                              Permanently remove selected values
                            </div>
                          </div>
                        </div>
                      </button>
                    </div>

                    {/* Replace Value Input */}
                    {operationMode === "replace" && (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Replace with:
                          </label>
                          <input
                            type="text"
                            value={replaceValue}
                            onChange={(e) => setReplaceValue(e.target.value)}
                            placeholder="Enter replacement value"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                          />
                        </div>

                        {/* Suggested Values */}
                        {filteredSuggestedValues.length > 0 && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Popular values in this column:
                            </label>
                            <div className="grid grid-cols-2 gap-2">
                              {filteredSuggestedValues
                                .slice(0, 4)
                                .map((suggestion, index) => (
                                  <button
                                    key={index}
                                    onClick={() =>
                                      handleSelectSuggestedValue(
                                        suggestion.value,
                                      )
                                    }
                                    className={`text-left p-3 border rounded-lg transition-colors hover:bg-gray-50 ${
                                      replaceValue === suggestion.value
                                        ? "border-primary bg-primary/5"
                                        : "border-gray-200"
                                    }`}
                                    type="button"
                                  >
                                    <div className="font-medium text-sm text-gray-900 truncate">
                                      {suggestion.value}
                                    </div>
                                    <div className="text-xs text-gray-600">
                                      {(suggestion.count || 0).toLocaleString()}{" "}
                                      files
                                    </div>
                                  </button>
                                ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Preview and Execute */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-left">
                  <div className="flex items-center gap-3">
                    <BsLightning className="w-5 h-5 text-amber-600" />
                    <div>
                      <h4 className="font-medium text-amber-900">
                        Preview Changes
                      </h4>
                      <p className="text-sm text-amber-700">
                        Review the changes below before applying them to your
                        data.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Preview Results */}
                <div className="bg-white border border-gray-200 rounded-lg max-h-[300px] overflow-y-auto text-left">
                  {previewResults.map((preview, index) => (
                    <div
                      key={index}
                      className="p-4 border-b border-gray-100 last:border-b-0"
                    >
                      <div className="flex items-center justify-between">
                        <div className="text-sm flex-1">
                          <span className="text-red-600 line-through font-medium">
                            {preview.original}
                          </span>
                          <FaArrowRight className="inline mx-3 text-gray-400" />
                          <span
                            className={`font-medium ${
                              preview.operation === "delete"
                                ? "text-red-600"
                                : "text-primary"
                            }`}
                          >
                            {preview.preview}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Summary */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-sm text-gray-600">
                    <strong>Summary:</strong> This will{" "}
                    {operationMode === "replace" ? "replace" : "delete"}{" "}
                    <strong>{selectedResults.size}</strong> unique values for{" "}
                    <strong>{column}</strong> across your data connector{" "}
                    <strong>{vdbLastActiveProfileName}</strong>.
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer with Navigation */}
        <div className="flex-shrink-0 bg-gray-50 border-t border-gray-200 p-6">
          <div className="flex justify-between items-center">
            <div>
              {currentStep > 1 && (
                <button
                  onClick={() => {
                    const newStep = currentStep - 1;
                    setCurrentStep(newStep);

                    // Only clear search results when going back to step 1
                    if (newStep === 1) {
                      setCurrentPage(1);
                      setSearchResults([]);
                      setHasMoreResults(false);
                      setTotalResultCount(0);
                      setCurrentLoadedCount(0);
                    }
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={isProcessing}
                >
                  Back
                </button>
              )}
            </div>
            <div className="flex gap-3">
              {/* Search Button */}
              {currentStep === 1 && (
                <button
                  onClick={() => performSearch(false)}
                  disabled={isSearching || !searchPattern.trim()}
                  className="bg-primary text-white py-2 px-6 rounded-lg font-medium disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors"
                >
                  {isSearching ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Searching...
                    </>
                  ) : (
                    <>
                      <FaSearch />
                      Find Values
                    </>
                  )}
                </button>
              )}
              {currentStep === 2 && (
                <button
                  onClick={generatePreview}
                  disabled={operationMode === "replace" && !replaceValue.trim()}
                  className="bg-primary text-white py-2 px-6 rounded-lg font-medium disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors"
                >
                  <BsLightning />
                  Preview Changes
                </button>
              )}
              {currentStep === 3 && (
                <button
                  onClick={executeOperation}
                  disabled={isProcessing}
                  className={`px-6 py-2 text-sm font-medium text-white rounded-lg transition-colors flex items-center gap-2 ${
                    operationMode === "replace"
                      ? "bg-primary hover:bg-primary/90"
                      : "bg-red-600 hover:bg-red-700"
                  } ${isProcessing ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  {isProcessing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      {operationMode === "replace" ? (
                        <FaExchangeAlt />
                      ) : (
                        <FaTrash />
                      )}
                      {operationMode === "replace"
                        ? "Apply Replacement"
                        : "Delete Values"}
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default FindReplacePanel;
